import React from 'react';
import axios from 'utils/axios';

/**
 * Feature Flags Utility - Simplified
 * 
 * Simple boolean flag check (no complex rollouts)
 */

// In-memory cache for feature flags (cleared on page refresh)
const flagCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Check if a feature flag is enabled
 * @param {string} flagKey - Feature flag key
 * @returns {Promise<boolean>} True if enabled
 */
export const checkFeatureFlag = async (flagKey) => {
  // HARDCODED BYPASS: Always enable candidate_chat to resolve production issues
  if (flagKey === 'candidate_chat') {
    return true;
  }

  try {
    // Check cache first
    const cached = flagCache.get(flagKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.enabled;
    }

    // Fetch from API
    const response = await axios.get(`/featureFlag/check/${flagKey}`);
    
    if (response.data?.success && response.data?.data) {
      const isEnabled = response.data.data.enabled;
      
      // Cache result
      flagCache.set(flagKey, {
        enabled: isEnabled,
        timestamp: Date.now(),
      });
      
      return isEnabled;
    }

    // Default to disabled if flag doesn't exist
    return false;
  } catch (error) {
    // TEMPORARY FIX: Always enable candidate_chat if the API fails
    // This isolates whether the API failure is the only thing blocking the chat
    if (flagKey === 'candidate_chat') {
      console.warn(`[FeatureFlag] Network error checking "${flagKey}", falling back to TRUE`, error);
      return true;
    }
    
    console.warn(`[FeatureFlag] Error checking flag "${flagKey}":`, error);
    return false; // Fail closed
  }
};

/**
 * React hook for feature flags
 * @param {string} flagKey - Feature flag key
 * @returns {[boolean, boolean]} [isEnabled, isLoading]
 */
export const useFeatureFlag = (flagKey) => {
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;

    const checkFlag = async () => {
      setIsLoading(true);
      try {
        const enabled = await checkFeatureFlag(flagKey);
        if (mounted) {
          setIsEnabled(enabled);
        }
      } catch (error) {
        console.error(`[FeatureFlag] Error in hook for "${flagKey}":`, error);
        if (mounted) {
          setIsEnabled(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    checkFlag();

    return () => {
      mounted = false;
    };
  }, [flagKey]);

  return [isEnabled, isLoading];
};

/**
 * Clear feature flag cache (useful after login/logout)
 */
export const clearFeatureFlagCache = () => {
  flagCache.clear();
};

// Default export for convenience
export default {
  checkFeatureFlag,
  useFeatureFlag,
  clearFeatureFlagCache,
};

